<!-- =========================================================================================
  File Name: UserView.vue
  Description: User View page
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>
	<div id="page-company-view">
		<vs-alert color="danger" title="User Not Found" :active.sync="company_not_found">
			<span>{{ $t("UserRecordWithId") + ":" + $route.params.userId + " " + $t("NotFound") }}. </span>
			<span>
				<span>{{ $t("Check") }} </span>
				><router-link :to="{name: 'page-user-list'}" class="text-inherit underline">All Users</router-link>
			</span>
		</vs-alert>
		<div id="user-data">
			<vx-card title="" class="mb-base" no-shadow>
				<session-api-company-data v-if="company" :item="company" />
				<vs-divider />
				<session-api-navbar :handleOption="handleOption" :selected="selected" />
				<session-api-list
					:handleItemSelected="handleItemSelected"
					:handleOption="handleOption"
					:clearSelectionTrigger="clearSelectionTrigger"
				></session-api-list>
			</vx-card>
		</div>
		<vs-prompt
			:title="promptTitle.toLowerCase() | title"
			:accept-text="update ? $t('Crud.Update') : $t('Crud.Create')"
			:cancel-text="$t('Cancel')"
			button-cancel="border"
			:active.sync="activePrompt"
			:is-valid="validateForm"
			@cancel="clearFields"
			@accept="update ? updateSessionApi() : createNewSessionApi()"
			@close="clearFields"
		>
			<form>
				<span v-if="update">
					{{ $t("SessionApi.AccessToken") }}: <br />
					<span class="text-sm font-semibold">{{ selected.access_token }}</span>
				</span>
				<span v-else>{{ $t("SessionApi.GenerateAccessToken") }}</span>
				<div class="vx-row">
					<div class="vx-col w-full mt-2" v-show="!update">
						<label class="vs-input--label text-primary">{{
							$t("CompanyModule.AdminResource").toLowerCase() | title
						}}</label>
						<v-select
							label="username"
							:options="resources"
							:dir="$vs.rtl ? 'rtl' : 'ltr'"
							v-model="resource"
						>
							<template v-slot:no-options="{search, searching}">
								<template v-if="searching">
									{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
								</template>
								<template v-else>
									{{ $t("NoResultsFound") }}
								</template>
							</template>
						</v-select>
					</div>
					<div class="vx-col w-full mt-2 mb-base">
						<small class="date-label"> {{ expireDateLabelText.toLowerCase() | title }}</small>
						<datepicker
							:language="$vs.rtl ? langHe : langEn"
							name="expireDate"
							v-validate="'validatorExpireDate'"
							v-model="expireDate"
							:disabledDates="disabledDatesTo"
						></datepicker>
						<span class="text-danger text-sm" v-show="errors.has('expireDate')"
							>{{ $t("SessionApi.ExpireDateError") }}:
							<code>{{ company ? new Date(company.date_end).toLocaleDateString() : "" }}</code></span
						>
					</div>
				</div>
			</form>
		</vs-prompt>

		<vs-prompt
			class="export-options"
			:title="exportType === 'EXPORT' ? $t('UploadFiles.Download') + ' ' + 'Token' : $t('ExportAll')"
			:accept-text="exportType === 'EXPORT' ? $t('UploadFiles.Download') : $t('ExportAll')"
			:cancel-text="$t('Cancel')"
			:active.sync="activeExportPrompt"
			@cancel="clearFieldsExport"
			@accept="exportToExcel"
			@close="clearFieldsExport"
		>
			<vs-input v-model="fileName" placeholder="Enter File Name.." class="w-full" />
			<v-select v-model="selectedFormat" :options="formats" class="my-4">
				<template v-slot:no-options="{search, searching}">
					<template v-if="searching">
						{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
					</template>
					<template v-else>
						{{ $t("NoResultsFound") }}
					</template>
				</template>
			</v-select>
			<div class="flex">
				<span class="mr-4">{{ $t("Routing.Map.CellAutoWith") }}:</span>
				<vs-switch v-model="cellAutoWidth">{{ $t("Routing.Map.CellAutoWith") }}</vs-switch>
			</div>
		</vs-prompt>
	</div>
</template>

<script>
// Components
import vSelect from "vue-select";
import Datepicker from "vuejs-datepicker";
import {en, es} from "vuejs-datepicker/src/locale";
import {Validator} from "vee-validate";

// Stores
import moduleCompany from "@/store/company/moduleCompany.js";
import moduleResource from "@/store/resource/moduleResource.js";

// Custom Components
import SessionApiList from "./SessionApiList.vue";
import SessionApiNavbar from "./SessionApiNavbar.vue";
import SessionApiCompanyData from "./SessionApiCompanyData.vue";

// Helpers
import DateUtils from "@/helpers/DateUtils.js";
import moment from "moment";

export default {
	components: {
		vSelect,
		Datepicker,
		SessionApiList,
		SessionApiNavbar,
		SessionApiCompanyData,
	},
	data() {
		return {
			company_not_found: false,
			selected: null,
			company: null,
			clearSelectionTrigger: 0,
			activePrompt: false,
			resource: null,
			langHe: es,
			langEn: en,
			update: false,
			fileName: "",
			expireToken: new Date(),
			formats: ["xlsx", "csv", "txt"],
			cellAutoWidth: true,
			selectedFormat: "xlsx",
			headerTitle: ["Token", this.$t("Email"), this.$t("Username"), this.$t("SessionApi.ExpireDate")],
			headerVal: ["access_token", "email", "username", "expire_token"],
			activeExportPrompt: false,
			exportType: "EXPORT",
			userInfo: JSON.parse(localStorage.getItem("userInfo")),
		};
	},
	computed: {
		resources() {
			return this.$store.state.moduleResource.resources;
		},
		validateForm() {
			return !this.errors.any() && this.resource !== null && Object.entries(this.resource).length > 0;
		},
		disabledDatesTo() {
			return {to: new Date()};
		},
		promptTitle() {
			return this.update
				? `${this.$t("Crud.Update")} ${this.$t("SessionApi.AccessToken")}`
				: `${this.$t("Crud.Create")} ${this.$t("SessionApi.AccessToken")}`;
		},
		expireDateLabelText() {
			return this.update
				? `${this.$t("Crud.Update")} ${this.$t("SessionApi.ExpireDate")}`
				: this.$t("SessionApi.ExpireDate");
		},
		expireDate: {
			get() {
				if (!this.expireToken) return null;
				const fecha = moment.utc(this.expireToken);
				if (!fecha.isValid()) {
					return null;
				}
				if (this.update && this.selected) {
					return fecha.format("DD MMM YYYY hh:mm:ss a");
				}
				return fecha.toDate();
			},
			set(val) {
				this.expireToken = val;
			},
		},
	},
	methods: {
		openLoading() {
			this.$vs.loading();
		},
		closeLoading() {
			this.$vs.loading.close();
		},
		handleItemSelected(val) {
			this.expireToken = val.expire_token;
			this.selected = val;
		},
		handleOption(option) {
			switch (option) {
				case "CREATE":
					this.expireToken = new Date();
					this.resource = null;
					this.activePrompt = true;
					break;
				case "UPDATE":
					this.activePrompt = true;
					this.update = true;
					this.resource = this.resources.find((resource) => resource.username === this.selected.username);
					break;
				case "DELETE":
					this.confirmDeleteSessionApi();
					break;
				case "EXPORT":
				case "EXPORT_ALL":
					this.exportType = option;
					this.activeExportPrompt = true;
					break;
				case "CLEAR_SELECTION":
					this.selected = null;
					this.clearSelectionTrigger += 1;
					break;
				default:
					break;
			}
		},
		clearFields() {
			this.resource = null;
			this.update = false;
		},
		confirmDeleteSessionApi() {
			this.$vs.dialog({
				type: "confirm",
				color: "danger",
				title: `${this.$t("Confirm")} ${this.$t("Crud.Delete")}`,
				text: `${this.$t("Crud.Delete")} token:  ${this.selected.access_token}`,
				accept: this.deleteSessionApi,
				acceptText: this.$t("Crud.Delete"),
				cancelText: this.$t("Cancel"),
			});
		},
		async deleteSessionApi() {
			try {
				this.openLoading();
				await this.$store.dispatch("moduleSessionApi/removeSessionApi", this.selected.access_token);
				this.handleOption("CLEAR_SELECTION");
				this.closeLoading();
				this.$vs.notify({
					color: "success",
					title: this.$t("SessionApi.Messages.TitleConfirmDelete"),
					text: this.$t("SessionApi.Messages.TextConfirmDelete"),
				});
			} catch (error) {
				this.closeLoading();
				console.error(error);
			}
		},
		async getCompany() {
			const companyId = this.$route.params.id ? this.$route.params.id : +this.userInfo.serviroutes_company_id;
			try {
				const response = await this.$store.dispatch("moduleCompany/getCompanyId", companyId);
				if (Array.isArray(response.data) && response.data.length > 0) {
					this.company = response.data[0];
					const visualConfig = this.$store.getters["moduleCompanyConfig/visualConfig"];
					if (visualConfig && visualConfig.config_json) {
						const {url_logo} = visualConfig.config_json;
						this.company.url_logo = url_logo ? url_logo : null;
					}
				} else {
					throw new Error("No company found");
				}
			} catch (error) {
				console.error(error);
				this.company_not_found = true;
			}
		},
		async createNewSessionApi() {
			const payload = {
				company_id: +this.$route.params.id,
				resource_id: this.resource.id,
				expire_token: DateUtils.toDefaultDateTime(this.expireDate, "YYYY-MM-DD"),
			};
			this.openLoading();
			try {
				await this.$store.dispatch("moduleSessionApi/createSessionsApi", payload);
				this.closeLoading();
				this.$vs.notify({
					color: "success",
					title: this.$t("SessionApi.Messages.TitleConfirmCreate"),
					text: this.$t("SessionApi.Messages.TextConfirmCreate"),
				});
			} catch (error) {
				this.closeLoading();
				console.error(error);
			}
		},
		async updateSessionApi() {
			this.openLoading();
			try {
				await this.$store.dispatch(
					"moduleSessionApi/updateSessionApi",
					Object.assign(this.selected, {
						expire_token: DateUtils.toDefaultDateTime(this.expireToken, "YYYY-MM-DD"),
					})
				);
				this.closeLoading();
				this.update = false;
				this.handleOption("CLEAR_SELECTION");
				this.$vs.notify({
					color: "success",
					title: this.$t("SessionApi.Messages.TitleConfirmUpdate"),
					text: this.$t("SessionApi.Messages.TextConfirmUpdate"),
				});
			} catch (error) {
				this.closeLoading();
				console.error(error);
			}
		},
		async getResources() {
			const payload = {
				sort: "desc",
				company_id: this.$route.params.id ? [parseInt(this.$route.params.id)] : [],
				role_name: ["ADMIN"],
			};
			this.openLoading();
			try {
				await this.$store.dispatch("moduleResource/fetchResourceFilterPaginate", payload);
				this.closeLoading();
			} catch (error) {
				this.closeLoading();
			}
		},
		/**
		 * Custom validator to check allow max expire date
		 * @param {Date} value Current date value selected on the piker
		 * @param {*} args
		 */
		validatorExpireDate(value, args) {
			if (!value || value === "" || typeof value === "number") {
				return true;
			}
			const expDate = typeof value === "string" ? new Date(value).getTime() : value.getTime();
			const companyDate =
				typeof this.company.date_end === "string"
					? new Date(this.company.date_end).getTime()
					: this.company.date_end.getTime();
			return expDate <= companyDate;
		},
		exportToExcel() {
			import("@/vendor/Export2Excel").then((excel) => {
				const list =
					this.exportType === "EXPORT"
						? [this.selected]
						: this.$store.getters["moduleSessionApi/getAllSessions"]();
				const data = this.formatJson(this.headerVal, list);
				excel.export_json_to_excel({
					header: this.headerTitle,
					data,
					filename: this.fileName,
					autoWidth: this.cellAutoWidth,
					bookType: this.selectedFormat,
				});
				this.clearFields();
			});
		},
		formatJson(filterVal, jsonData) {
			return jsonData.map((v) =>
				filterVal.map((j) => {
					return v[j];
				})
			);
		},
		clearFieldsExport() {
			this.fileName = "";
			this.cellAutoWidth = true;
			this.selectedFormat = "xlsx";
		},
	},
	created() {
		// Register Module Company Module
		if (!moduleCompany.isRegistered) {
			this.$store.registerModule("moduleCompany", moduleCompany);
			moduleCompany.isRegistered = true;
		}

		if (!moduleResource.isRegistered) {
			this.$store.registerModule("moduleResource", moduleResource);
			moduleResource.isRegistered = true;
		}

		// Register custom validator
		Validator.extend("validatorExpireDate", this.validatorExpireDate);
		if (this.$route.params.company) {
			this.company = this.$route.params.company;
		} else {
			this.getCompany();
		}
		this.getResources();
	},
};
</script>

<style lang="scss">
#page-company-view {
	background-color: white;
	table {
		td {
			vertical-align: top;
			min-width: 140px;
			padding-bottom: 0.8rem;
			word-break: break-all;
		}

		&:not(.permissions-table) {
			td {
				@media screen and (max-width: 370px) {
					display: block;
				}
			}
		}
	}
}
@media screen and (min-width: 1201px) and (max-width: 1211px),
	only screen and (min-width: 636px) and (max-width: 991px) {
	#account-info-col-1 {
		width: calc(100% - 12rem) !important;
	}
}
</style>
